import { t } from '../i18n.js';

/**
 * Helper function to replace placeholders in translation strings
 * @param {string} key - Translation key
 * @param {Object} params - Object with placeholder values
 * @returns {string} Translated string with replaced placeholders
 */
function tt(key, params = {}) {
  let str = t(key);
  for (const [key, value] of Object.entries(params)) {
    str = str.replace(`{${key}}`, value);
  }
  return str;
}

class PushNotificationComposer extends HTMLElement {

    // ── A. Shadow DOM setup ──────────────────────────────────────────────
    constructor() {
      super();
      this._shadow = this.attachShadow({ mode: "open" });
      this._users  = [];
    }

    // ── B. Lifecycle: element added to DOM ───────────────────────────────
    connectedCallback() {
      this._render();
      this._bindEvents();
    }

    // ── C. JS property setter ────────────────────────────────────────────
    set users(data) {
      this._users = Array.isArray(data) ? data : [];
      this._buildOptions();
      this._syncFooter();
    }

    // ── D. Attribute → property bridge ───────────────────────────────────
    static get observedAttributes() { return ["users"]; }
    attributeChangedCallback(name, _, newVal) {
      if (name === "users") {
        try { this.users = JSON.parse(newVal); } catch {}
      }
    }

    // ── E. Utility: initials ─────────────────────────────────────────────
    _initials(u) {
      return (u.name[0] + u.surname[0]).toUpperCase();
    }

    // ── F. Push-enabled users only ───────────────────────────────────────
    _pushUsers() {
      return this._users.filter(u => u.push);
    }

    // ── G. Build <select> options from users array ───────────────────────
    _buildOptions() {
      const select = this._shadow.getElementById("pn-select");
      if (!select) return;
      select.innerHTML = "";

      const pushUsers = this._pushUsers();

      // "Everyone" option first
      const everyoneOpt = document.createElement("option");
      everyoneOpt.value = "everyone";
      everyoneOpt.textContent = `${tt("pn-everyone")} (${tt("pn-users-with-push", { count: pushUsers.length })})`;
      select.appendChild(everyoneOpt);

      // One option per push-enabled user
      pushUsers.forEach(u => {
        const opt = document.createElement("option");
        opt.value = u.id;
        opt.textContent = `${u.name} ${u.surname} · @${u.alias}`;
        select.appendChild(opt);
      });
    }

    // ── H. Sync avatar preview + footer label ────────────────────────────
    _syncFooter() {
      const select       = this._shadow.getElementById("pn-select");
      const avatarEl     = this._shadow.getElementById("pn-avatar");
      const footerLabel  = this._shadow.getElementById("pn-footer-label");
      if (!select || !avatarEl || !footerLabel) return;

      const val       = select.value;
      const pushUsers = this._pushUsers();

      if (val === "everyone") {
        avatarEl.innerHTML = this._everyoneIcon();
        avatarEl.className = "avatar avatar-everyone";
        footerLabel.innerHTML = `${tt("pn-to")} <strong>${tt("pn-everyone")} (${pushUsers.length} users)</strong>`;
      } else {
        const u = pushUsers.find(x => x.id === Number(val));
        if (!u) return;
        avatarEl.innerHTML = u.avatar
          ? `<img src="${u.avatar}" alt="${u.name}" onerror="this.style.display='none'">`
          : this._initials(u);
        avatarEl.className = "avatar";
        footerLabel.innerHTML = `${tt("pn-to")} <strong>${u.name} ${u.surname} (@${u.alias})</strong>`;
      }
    }

    // ── I. Validate and toggle send button ───────────────────────────────
    _updateSendBtn() {
      const msg     = this._shadow.getElementById("pn-message").value.trim();
      const sendBtn = this._shadow.getElementById("pn-send");
      if (sendBtn) sendBtn.disabled = msg.length === 0;
    }

    // ── J. Character counter ─────────────────────────────────────────────
    _updateCharCount() {
      const msg      = this._shadow.getElementById("pn-message");
      const counter  = this._shadow.getElementById("pn-chars");
      if (!msg || !counter) return;
      const len = msg.value.length;
      counter.textContent = `${len} / 256`;
      counter.className = "char-count"
        + (len > 230 ? " over" : len > 180 ? " warn" : "");
    }

    // ── K. Bind events ───────────────────────────────────────────────────
    _bindEvents() {
      const root = this._shadow;

      root.getElementById("pn-select").addEventListener("change", () => {
        root.getElementById("pn-toast").classList.remove("visible");
        this._syncFooter();
      });

      root.getElementById("pn-message").addEventListener("input", () => {
        this._updateCharCount();
        this._updateSendBtn();
      });

      root.getElementById("pn-send").addEventListener("click", () => {
        this._handleSend();
      });
    }

    // ── L. Handle send ───────────────────────────────────────────────────
    _handleSend() {
      const root    = this._shadow;
      const val     = root.getElementById("pn-select").value;
      const msg     = root.getElementById("pn-message").value.trim();
      if (!msg) return;

      const pushUsers  = this._pushUsers();
      const recipient  = val === "everyone"
        ? "everyone"
        : pushUsers.find(u => u.id === Number(val));

      // Fire custom event to host page
      this.dispatchEvent(new CustomEvent("notification-send", {
        detail: { recipient, message: msg },
        bubbles: true,
        composed: true,
      }));

      // Show toast
      const label = val === "everyone"
        ? `${tt("pn-everyone")} (${pushUsers.length} users)`
        : `${recipient.name} ${recipient.surname}`;
      root.getElementById("pn-toast-text").textContent = tt("pn-sent-to", { label });
      root.getElementById("pn-toast").classList.add("visible");

      // Reset form
      root.getElementById("pn-message").value = "";
      root.getElementById("pn-chars").textContent = "0 / 256";
      root.getElementById("pn-chars").className = "char-count";
      root.getElementById("pn-send").disabled = true;
    }

    // ── M. Inline SVG icon for "everyone" ────────────────────────────────
    _everyoneIcon() {
      return `<svg width="14" height="14" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
        <circle cx="6" cy="5" r="2.5" stroke="currentColor" stroke-width="1.2"/>
        <path d="M1.5 13c0-2.485 2.015-4.5 4.5-4.5s4.5 2.015 4.5 4.5" stroke="currentColor" stroke-width="1.2" stroke-linecap="round"/>
        <circle cx="11.5" cy="5" r="2" stroke="currentColor" stroke-width="1.2"/>
        <path d="M13.5 12.5c0-1.657-1.119-3-2.5-3" stroke="currentColor" stroke-width="1.2" stroke-linecap="round"/>
      </svg>`;
    }

    // ── N. Full initial render ────────────────────────────────────────────
    _render() {
      this._shadow.innerHTML = `
        <style>
          *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }

          :host {
            display: block;
            /*font-family: system-ui, sans-serif;*/
            color: #1a1a1a;
          }

          .card {
            background: #fff;
            border: 0.5px solid #e0dfd7;
            border-radius: 12px;
            padding: 1.25rem;
            /* max-width: 700px; */
          }

          .field { margin-bottom: 14px; }

          label {
            display: block;
            font-size: 12px;
            font-weight: 500;
            color: #888;
            margin-bottom: 6px;
            letter-spacing: 0.02em;
          }

          .select-wrap {
            position: relative;
            display: flex;
            align-items: center;
          }

          .avatar {
            position: absolute;
            left: 10px;
            width: 24px;
            height: 24px;
            border-radius: 50%;
            background: #ddeef9;
            color: #2a6496;
            display: flex;
            align-items: center;
            justify-content: center;
            font-size: 10px;
            font-weight: 500;
            pointer-events: none;
            overflow: hidden;
            flex-shrink: 0;
            z-index: 1;
          }

          .avatar img {
            width: 100%;
            height: 100%;
            object-fit: cover;
            border-radius: 50%;
          }

          .avatar-everyone {
            background: #A28077;
            color: #F5EDEB;
          }

          select, textarea {
            font: inherit;
            font-size: 14px;
            border: 0.5px solid #d0cfc7;
            border-radius: 8px;
            background: #fff;
            color: #888;
            outline: none;
            width: 100%;
          }

          select {
            padding: 8px 12px 8px 44px;
            appearance: none;
            -webkit-appearance: none;
            background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 12 12'%3E%3Cpath d='M2 4l4 4 4-4' stroke='%23888' stroke-width='1.2' fill='none' stroke-linecap='round'/%3E%3C/svg%3E");
            background-repeat: no-repeat;
            background-position: right 12px center;
            padding-right: 32px;
            cursor: pointer;
          }

          select:focus, textarea:focus { border-color: #888; }

          textarea {
            padding: 10px 12px;
            min-height: 120px;
            resize: vertical;
            line-height: 1.5;
          }

          .char-count {
            font-size: 11px;
            color: #aaa;
            text-align: right;
            margin-top: 4px;
          }

          .char-count.warn { color: #b45309; }
          .char-count.over { color: #c0392b; }

          .divider {
            height: 0.5px;
            background: #e0dfd7;
            margin: 14px 0;
          }

          .footer {
            display: flex;
            align-items: center;
            justify-content: space-between;
            gap: 12px;
            margin-top: 4px;
          }

          .footer-label {
            font-size: 12px;
            color: #aaa;
          }

          .footer-label strong {
            color: #555;
            font-weight: 500;
          }

          .send-btn {
            display: flex;
            align-items: center;
            gap: 8px;
            padding: 8px 20px;
            font: inherit;
            font-size: 14px;
            font-weight: 500;
            cursor: pointer;
            border-radius: 8px;
            /*background: #1a1a1a;*/
            background: #5D4037;
            color: #fff;
            border: none;
            transition: opacity 0.15s, transform 0.1s;
            flex-shrink: 0;
          }

          .send-btn:hover:not(:disabled) { opacity: 0.82; }
          .send-btn:active:not(:disabled) { transform: scale(0.97); }
          .send-btn:disabled { opacity: 0.35; cursor: not-allowed; }

          .toast {
            display: none;
            align-items: center;
            gap: 8px;
            background: #e6f4ea;
            color: #2d6a4f;
            font-size: 13px;
            padding: 10px 14px;
            border-radius: 8px;
            border: 0.5px solid #b7dfbf;
            margin-top: 12px;
          }

          .toast.visible { display: flex; }

          .toast-dot {
            width: 7px;
            height: 7px;
            border-radius: 50%;
            background: #2d6a4f;
            flex-shrink: 0;
          }

          @media (max-width: 400px) {
            .card { padding: 1rem; }
            .send-btn { padding: 8px 14px; }
          }
        </style>

        <div class="card">
        <div class="field">
          <label for="pn-select">${tt("pn-send-to")}</label>
          <div class="select-wrap">
            <div class="avatar avatar-everyone" id="pn-avatar">
              ${this._everyoneIcon()}
            </div>
            <select id="pn-select">
              <option value="everyone">${tt("pn-loading")}</option>
            </select>
          </div>
        </div>

        <div class="divider"></div>

        <div class="field">
          <label for="pn-message">${tt("pn-message")}</label>
          <textarea id="pn-message" placeholder="${tt("pn-message-placeholder")}" maxlength="256"></textarea>
          <p class="char-count" id="pn-chars">0 / 256</p>
        </div>

        <div class="footer">
          <p class="footer-label" id="pn-footer-label">${tt("pn-to")} <strong>${tt("pn-everyone")}</strong></p>
          <button class="send-btn" id="pn-send" disabled>
            <svg width="14" height="14" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M14 2L7.5 8.5M14 2L9.5 14L7.5 8.5M14 2L2 6.5L7.5 8.5" stroke="currentColor" stroke-width="1.4" stroke-linecap="round" stroke-linejoin="round"/>
            </svg>
            ${tt("pn-send")}
          </button>
        </div>

        <div class="toast" id="pn-toast">
          <span class="toast-dot"></span>
          <span id="pn-toast-text">${tt("pn-sent-success")}</span>
        </div>
        </div>
      `;
    }
  }

export { PushNotificationComposer }
